<template>
  <div class="user-posts-container">
    <h2 class="page-title">我的帖子</h2>
    
    <!-- 状态筛选按钮组 -->
    <div class="status-filter">
      <button 
        v-for="status in statuses" 
        :key="status.value"
        :class="['status-btn', { active: selectedStatus === status.value }]"
        @click="selectStatus(status.value)"
      >
        {{ status.label }}
      </button>
    </div>
    
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="posts.length === 0" class="no-data">暂无帖子内容</div>
    <div v-else class="posts-list">
      <div 
        v-for="post in posts" 
        :key="post.id" 
        class="post-card"
        @click="$emit('show-post-detail', post.id)"
      >
        <div class="post-header">
          <img :src="post.userAvatar" alt="avatar" class="avatar" />
          <div class="user-info">
            <div class="username">{{ post.name }}</div>
            <div class="post-time">{{ formatDate(new Date(post.createTime)) }}</div>
          </div>
          <div class="post-status" :class="'status-' + post.status">
            {{ getStatusText(post.status) }}
          </div>
        </div>
        <div class="post-content">
          <h3 class="post-title">{{ post.title }}</h3>
          <p class="post-text">{{ post.content }}</p>
          <img v-if="post.coverImage" :src="post.coverImage" alt="cover" class="post-cover" />
        </div>
        <div class="post-stats">
          <span class="stat-item">
            <el-icon><View /></el-icon>{{ post.viewCount }}
          </span>
          <span class="stat-item">
            <el-icon><StarFilled /></el-icon>{{ post.likeCount }}
          </span>
          <span class="stat-item">
            <el-icon><ChatLineRound /></el-icon>{{ post.commentCount }}
          </span>
          <span class="stat-item">
            <el-icon><Collection /></el-icon>{{ post.favoriteCount }}
          </span>
        </div>
        
        <!-- 操作按钮组，仅当帖子发布者是当前用户时显示 -->
        <div v-if="post.userId === currentUserId" class="action-buttons">
          <button 
            class="edit-btn"
            @click.stop="openEditModal(post.id)"
          >
            ✏️ 编辑
          </button>
          <button 
            class="delete-btn"
            @click.stop="handleDelete(post.id)"
          >
            🗑️ 删除
          </button>
        </div>
      </div>
    </div>
    <el-pagination
      v-if="total > pageSize"
      class="pagination"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="total"
      layout="prev, pager, next"
      @current-change="handlePageChange"
    />

    <!-- 编辑帖子模态框 -->
    <EditPostModal
      v-model="editDialogVisible"
      :post-id="editingPostId"
      @save-success="onEditSuccess"
      @close="onModalClose"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { View, StarFilled, ChatLineRound, Collection } from '@element-plus/icons-vue';
import { getUserPosts } from '@/user/api/userApi';  // 从userApi导入获取用户帖子列表
import { deletePost } from '@/user/api/post'; // 从正确的API文件导入deletePost函数
import { formatPostDate } from '@/utils/date';
import { useStore } from 'vuex'; // 导入store
import EditPostModal from './EditPostModal.vue'; // 引入新的编辑模态框组件

interface Post {
  id: number;
  userId: number;
  name: string;
  userAvatar: string;
  title: string;
  content: string;
  coverImage?: string;
  status: number;
  createTime: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  isLiked: number;
  isFavorited: number;
  isFollowed: number;
  hotComment: any;
  comments: any[];
}

const posts = ref<Post[]>([]);
const loading = ref(true);
const currentPage = ref(1);
const total = ref(0);
const pageSize = ref(5);

// 编辑帖子相关
const editDialogVisible = ref(false);
const editingPostId = ref<number | null>(null);

// 状态筛选相关
const statuses = [
  { label: '全部', value: -1 },
  { label: '已发布', value: 1 },
  { label: '待审核', value: 0 },
  { label: '已驳回', value: 2 },
  { label: '已封禁', value: 3 }
];

const selectedStatus = ref(-1); // 默认选中全部

// 从store获取当前用户ID
const store = useStore();
const currentUserId = computed(() => store.state.userInfo?.id || null);

// 格式化日期
const formatDate = (date: Date) => {
  return formatPostDate(date);
};

// 获取状态文本
const getStatusText = (status: number) => {
  switch(status) {
    case 0: return '待审核';
    case 1: return '已发布';
    case 2: return '已驳回';
    case 3: return '封禁';
    default: return '全部';
  }
};

// 获取用户帖子列表
const loadUserPosts = async (status = -1) => {
  try {
    loading.value = true;
    // 如果是全部(-1)，则不传递状态参数
    const params: any = {
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    if (status !== -1) {
      params.status = status;
    }
    
    const response = await getUserPosts(params);
    
    // 处理可能的数据结构差异
    let responseData;
    if (response.data) {
      // 如果响应在外层有一个data包装
      responseData = response.data;
    } else {
      // 如果响应直接就是数据
      responseData = response;
    }
    
    // 检查数据结构，兼容不同格式
    if (responseData && responseData.records) {
      // 标准格式: { records: [...], total: x }
      posts.value = responseData.records || [];
      total.value = responseData.total || 0;
    } else if (Array.isArray(responseData)) {
      // 直接数组格式: [...]
      posts.value = responseData;
      total.value = responseData.length;
    } else if (responseData && responseData.data && responseData.data.records) {
      // 双层包装格式: { data: { records: [...], total: x }}
      posts.value = responseData.data.records || [];
      total.value = responseData.data.total || 0;
    } else {
      // 尝试其他可能的字段名
      const possibleRecordsKeys = ['records', 'data', 'list', 'items'];
      let foundRecords = false;
      
      for (const key of possibleRecordsKeys) {
        if (responseData[key]) {
          posts.value = Array.isArray(responseData[key]) ? responseData[key] : [];
          foundRecords = true;
          break;
        }
      }
      
      if (!foundRecords) {
        posts.value = [];
      }
      
      // 查找总数字段
      const possibleTotalKeys = ['total', 'totalCount', 'count'];
      for (const key of possibleTotalKeys) {
        if (responseData[key] !== undefined) {
          total.value = responseData[key] || 0;
          break;
        }
      }
    }
  } catch (error) {
    console.error('获取用户帖子失败:', error);
    ElMessage.error('获取用户帖子失败');
  } finally {
    loading.value = false;
  }
};

// 打开编辑模态框
const openEditModal = (postId: number) => {
  // 先重置postId，然后在下一个tick设置新值，确保watch触发
  editingPostId.value = null;
  nextTick(() => {
    editingPostId.value = postId;
    editDialogVisible.value = true;
  });
};

// 编辑成功回调
const onEditSuccess = () => {
  editDialogVisible.value = false;
  editingPostId.value = null; // 重置postId
  loadUserPosts(selectedStatus.value);
};

// 模态框关闭回调
const onModalClose = () => {
  editingPostId.value = null; // 重置postId
};

// 删除帖子
const handleDelete = async (postId: number) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个帖子吗？此操作不可撤销。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除帖子的API
    const response = await deletePost(postId);
    
    if (response.code === 1) {
      ElMessage.success('帖子删除成功');
      // 重新加载当前状态的帖子列表
      await loadUserPosts(selectedStatus.value);
    } else {
      ElMessage.error(response.msg || '删除帖子失败');
    }
  } catch (error) {
    // 用户取消删除或发生错误
    if (error !== 'cancel') {
      console.error('删除帖子失败:', error);
      ElMessage.error('删除帖子失败');
    }
  }
};

// 筛选状态
const selectStatus = async (status: number) => {
  selectedStatus.value = status;
  currentPage.value = 1; // 重置到第一页
  await loadUserPosts(status); // 加载指定状态的帖子
};

// 分页改变
const handlePageChange = (page: number) => {
  currentPage.value = page;
  loadUserPosts(selectedStatus.value); // 根据当前选中状态加载帖子
};

onMounted(() => {
  loadUserPosts(); // 页面初始化时加载全部帖子
});
</script>

<style scoped>
.user-posts-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: #f9f2eb;
  min-height: 100vh;
}

.page-title {
  text-align: center;
  color: #5a3e36;
  margin-bottom: 20px;
  font-size: 28px;
  font-weight: 600;
}

.status-filter {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.status-btn {
  padding: 8px 16px;
  border: 1px solid #d48341;
  background: #f8f3eb;
  color: #8b6d4b;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.status-btn.active,
.status-btn:hover {
  background: #d48341;
  color: white;
}

.loading,
.no-data {
  text-align: center;
  color: #8b6d4b;
  padding: 40px 0;
  font-size: 18px;
}

.posts-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.post-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(139, 109, 75, 0.1);
  border: 1px solid #e9dcc8;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
}

.post-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(139, 109, 75, 0.15);
}

.post-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #d4c19c;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
}

.user-info {
  flex: 1;
}

.username {
  font-weight: 600;
  color: #5a3e36;
  font-size: 16px;
}

.post-time {
  color: #8b6d4b;
  font-size: 12px;
  margin-top: 3px;
}

.post-status {
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
}

.status-0 {
  background: #f9e79f;
  color: #9c824a;
}

.status-1 {
  background: #85c1a3;
  color: white;
}

.status-2 {
  background: #e74c3c;
  color: white;
}

.status-3 {
  background: #5d4f87;
  color: white;
}

.post-content {
  margin-bottom: 15px;
}

.post-title {
  color: #5a3e36;
  font-size: 18px;
  margin: 0 0 10px 0;
  font-weight: 600;
  line-height: 1.4;
}

.post-text {
  color: #6b5e4c;
  line-height: 1.6;
  margin: 0 0 15px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-cover {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 10px;
  margin-top: 10px;
}

.post-stats {
  display: flex;
  justify-content: space-between;
  padding-top: 15px;
  border-top: 1px dashed #d4c19c;
  color: #8b6d4b;
  font-size: 14px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.stat-item .el-icon {
  color: #d48341;
}

.pagination {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

.action-buttons {
  position: absolute;
  bottom: 15px;
  right: 15px;
  display: flex;
  gap: 8px;
}

.edit-btn, .delete-btn {
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 12px;
}

.edit-btn {
  background-color: #d48341;
  color: white;
}

.delete-btn {
  background-color: #e74c3c;
  color: white;
}

.edit-btn:hover, .delete-btn:hover {
  opacity: 0.8;
}
</style>